import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { each, isEmpty, sum } from 'lodash';
import { trackError } from 'utils/helpers';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import { renderPassengerTypes } from 'components/passenger_types';
import { fetchCurrent } from 'redux/actions/booking';
import { getQuickCatOptions } from 'redux/selectors/experience';
import ContinueEditing from 'tenants/sealink/utils/routing/continue_editing';
import DrawerLayout from '../drawer_layout';
import { ExperienceContext } from '../helpers';
import Bedding from './bedding';
import Buttons from './buttons';
import Calendar from './calendar';
import ErrorDialog from './error_dialog';
import {
  checkQuickCatResource,
  getComplexApiData,
  getComplexBookableOption,
  getInitialValues,
  getMaximumPassenger,
  getPackageOptions,
  getQuickCatApiData,
  getReservationAction,
  getRoomTypeByPassengerCount,
  getRoomTypeOptions,
  getValidationRuls,
} from './helpers';
import OptionalExtras from './optional_extras';
import PickUpDropOff from './pick_up_drop_off';
import QuickCat from './quick_cat';

const DEFAULTPASSENGER_TYPES = [1, 2];

const BookingForm = () => {
  const { independentPassengerTypes, soloChildWarningMessage } =
    window.ecom.craftConfig?.bookingPreferences?.passengerForm || {};
  const location = useLocation();
  const dispatch = useDispatch();
  const experience = useSelector((state) => state.experience);
  const complexResource = useSelector(
    (state) => Object.values(state.resources)[0]
  );
  const isGiftVoucher = experience.gift_voucher;
  const {
    isCanvasOpen,
    departureFerry,
    returnFerry,
    needUserSelect,
    departureCoach: complexDeparture,
    returnCoach: complexReturn,
    setProductFeature,
  } = useContext(ExperienceContext);
  const { option, subGroup, back = true } = location.state || {};
  let isComplex = experience.complex;
  let showBack = back;
  let bookableOption;

  if (isComplex) {
    // If a complex package has neither complexDeparture nor complexReturn, e.g. resources/189
    // then treat it as a simple package
    if (complexDeparture === null && complexReturn === null) {
      isComplex = false;
      showBack = false;
      bookableOption = {
        resource_id: complexResource.id,
      };
    } else {
      showBack = Object.values(needUserSelect).some((value) => value);
      bookableOption = getComplexBookableOption(
        complexDeparture,
        complexReturn
      );
    }
  } else {
    ({ bookableOption } = location.state);
  }

  const resource = useSelector(
    (state) => state.resources[bookableOption.resource_id]
  );
  const isQuickCat = checkQuickCatResource(resource);
  const quickCatOptions = useSelector(getQuickCatOptions);
  const [passengerTypeIDs] = useState(() => {
    if (!isEmpty(bookableOption.passenger_types)) {
      return bookableOption.passenger_types.map((passenger) => passenger.id);
    }
    if (!isEmpty(experience.passenger_types)) {
      return experience.passenger_types.map((passenger) => passenger.id);
    }

    return DEFAULTPASSENGER_TYPES;
  });
  const roomTypeOptions = useMemo(
    () => getRoomTypeOptions(resource),
    [resource]
  );
  const packageOptions = useMemo(
    () => getPackageOptions(isComplex ? bookableOption : resource),
    [isComplex, bookableOption, resource]
  );
  const maximumPassenger = getMaximumPassenger({
    valueFromResource: resource.maximum_passengers,
    roomCapacity: bookableOption.room_capacity,
  });

  const bedRequirements = resource?.bed_requirements || [];
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorCode, setErrorCode] = useState();
  const { isEditing, isDraft } = useCurrentBookingState();
  const closeOffcanvasRef = useRef(
    document.querySelector(
      '#experience-off-canvas [data-bs-dismiss="offcanvas"]'
    )
  );
  const onSubmit = (values, { setSubmitting }) => {
    let params = {
      resource_id: resource.id,
      first_travel_date: values.date,
      passenger_types: values.passengers,
      bed_requirement_id: values.bedRequirement,
      discard_existing: false,
    };

    // Handle optional extras
    const totalExtras = sum(Object.values(values.optionalExtras));

    if (totalExtras) {
      const selections = {
        [values.optionalExtraGroupId]: {
          packageItems: {},
        },
      };

      each(values.optionalExtras, (val, key) => {
        if (val !== 0) {
          selections[values.optionalExtraGroupId].packageItems[key] = {
            selected: true,
            options: {
              quantity: val,
            },
          };
        }
      });
      params.selections = selections;
    }

    // Handle pick-up & drop-off parameters
    const subOptions = {};
    const { pickUps, dropOffs } = packageOptions;
    if (pickUps) {
      subOptions[pickUps.resource_group.id] = {
        from_route_stop_id: values.pickUp,
      };
    }
    if (dropOffs) {
      subOptions[dropOffs.resource_group.id] = {
        to_route_stop_id: values.dropOff,
      };
    }
    params.suboptions = isEmpty(subOptions) ? undefined : subOptions;

    if (isComplex) {
      params = getComplexApiData(
        resource,
        complexDeparture,
        complexReturn,
        departureFerry,
        returnFerry,
        values,
        bookableOption
      );
    }

    if (isQuickCat) {
      params = getQuickCatApiData({
        simpleParameters: params,
        formValues: values,
      });
    }

    setSubmitting(true);

    const errorHandling = (error) => {
      trackError(error);
      // Even though error occurred, booking might still have been created,
      // Need to update the booking in redux
      dispatch(fetchCurrent()).then(() => {
        setShowErrorDialog(true);
        setErrorCode(error?.code);
        closeOffcanvasRef.current?.click();
        setSubmitting(false);
      });
    };

    getReservationAction(resource.id)
      .then((action) => dispatch(action(params)))
      .then(() => {
        const tenantPath = ecom.craftConfig.api_prefix;

        // Only 1SLK sites have api_prefix
        window.location.assign(tenantPath ? `${tenantPath}/bookings` : '/cart');
      })
      .catch((error) => errorHandling(error));
  };

  const { hasAccommodation, hasFerry } = bookableOption;
  useEffect(() => {
    if (!isEditing && isCanvasOpen) {
      setProductFeature?.({ hasAccommodation, hasFerry });
    }
  }, [hasAccommodation, hasFerry, isEditing, setProductFeature, isCanvasOpen]);

  return (
    <DrawerLayout
      back={showBack}
      productName={isComplex ? bookableOption.resourceTitle : null}
      modifier={`experience-booking--${experience.section}`}
    >
      {(isEditing || isDraft) && <ContinueEditing reload />}
      <Formik
        initialValues={getInitialValues({
          packageOptions,
          passengerTypeIDs,
          bedRequirements,
          minPassenger: resource.minimum_passengers || 1,
          isGiftVoucher,
          isQuickCat,
          quickCatOptions,
        })}
        onSubmit={onSubmit}
        validationSchema={getValidationRuls({
          minPassenger: resource.minimum_passengers || 1,
          maxPassenger: maximumPassenger,
          independentPassengerTypes,
          soloChildWarningMessage,
        })}
      >
        {({ values, setFieldValue }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            const firstPassengerType = passengerTypeIDs[0];

            // Force Formik to validate based on the latest form data
            // https://github.com/jaredpalmer/formik/issues/2106
            setFieldValue(
              `passengers.${firstPassengerType}`,
              values.passengers[firstPassengerType]
            );
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [values.passengers]);

          const passengerCount = sum(Object.values(values.passengers));
          const roomType = getRoomTypeByPassengerCount(
            roomTypeOptions,
            passengerCount
          );

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            setFieldValue('roomType', roomType);
          }, [roomType, setFieldValue]);

          return (
            <>
              <Form className="pt-4">
                {option?.title && (
                  <h5 className="reset-text fw-bold mb-4">{option.title}</h5>
                )}
                {subGroup?.title && (
                  <h5 className="reset-text fw-bold mb-2">{subGroup.title}</h5>
                )}
                {resource.name && (
                  <h5 className="reset-text fw-bold mb-4 text-secondary">
                    {resource.name}
                  </h5>
                )}

                {(bookableOption.hasAccommodation ||
                  bookableOption.hasFerry) && (
                  <div className="mb-4">
                    {bookableOption.hasAccommodation && (
                      <p className="text-tertiary">
                        <i className="fas fa-check-circle icon__before-text text-success" />
                        Includes Accommodation
                      </p>
                    )}
                    {bookableOption.hasFerry && (
                      <p className="text-tertiary">
                        <i className="fas fa-check-circle icon__before-text text-success" />
                        Includes Ferry
                      </p>
                    )}
                  </div>
                )}

                {bookableOption.price && (
                  <h5 className="experience-booking__price mb-4">
                    {bookableOption.price}
                  </h5>
                )}

                {renderPassengerTypes(
                  passengerTypeIDs,
                  bookableOption.resource_id,
                  values.date,
                  'Passengers',
                  experience.passenger_message || null,
                  isGiftVoucher
                )}

                {!isGiftVoucher && (
                  <Calendar
                    resource={resource}
                    initialDate={experience.initialValues?.date}
                  />
                )}

                {!isEmpty(packageOptions) && (
                  <PickUpDropOff
                    locations={[
                      packageOptions.pickUps,
                      packageOptions.dropOffs,
                    ]}
                    bookableOption={bookableOption}
                  />
                )}
                {isQuickCat && <QuickCat options={quickCatOptions} />}
                {!isEmpty(bedRequirements) && (
                  <Bedding
                    selected={values.bedRequirement}
                    options={bedRequirements}
                  />
                )}
                <OptionalExtras resource={resource} />
                <Buttons
                  section={isGiftVoucher ? 'gift_vouchers' : experience.section}
                />
              </Form>
              {!isCanvasOpen && (
                <ErrorDialog
                  id="experience-booking-error-dialog"
                  show={showErrorDialog}
                  code={errorCode}
                  close={() => setShowErrorDialog(false)}
                />
              )}
            </>
          );
        }}
      </Formik>
    </DrawerLayout>
  );
};

export default BookingForm;
